<template>
  <el-popover
    :visible="visible"
    trigger="click"
    :width="380"
    placement="bottom-end"
    popper-class="notification-popover"
    @hide="onHide"
  >
    <template #reference>
      <el-badge
        :value="unreadCount > 0 ? unreadCount : ''"
        :max="99"
        class="notification-badge"
      >
        <el-tooltip
          content="消息通知"
          placement="bottom"
        >
          <el-icon
            class="header-icon notification-icon"
            @click="openPopover"
          >
            <Bell />
          </el-icon>
        </el-tooltip>
      </el-badge>
    </template>
    
    <div class="notification-container">
      <div class="notification-header">
        <div class="title">
          通知
        </div>
        <div class="actions">
          <el-button
            v-if="unreadCount > 0"
            size="small"
            type="primary"
            link
            @click="handleMarkAllAsRead"
          >
            全部已读
          </el-button>
          <el-button
            size="small"
            type="primary"
            link
            @click="navigateToNotificationCenter"
          >
            查看全部
          </el-button>
        </div>
      </div>
      
      <el-tabs
        v-model="activeTab"
        class="notification-tabs"
      >
        <el-tab-pane
          label="全部"
          name="all"
        />
        <el-tab-pane
          label="未读"
          name="unread"
        />
      </el-tabs>
      
      <div
        v-if="loading"
        class="notification-loading"
      >
        <el-skeleton
          :rows="3"
          animated
        />
      </div>
      
      <el-empty
        v-else-if="notifications.length === 0"
        description="暂无通知"
        :image-size="80"
      />
      
      <el-scrollbar
        v-else
        height="350px"
      >
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="notification-item"
          :class="{ 'is-read': notification.isRead }"
          @click="handleNotificationClick(notification)"
        >
          <div
            class="icon-container"
            :class="notification.severity"
          >
            <el-icon>
              <component :is="getIconComponent(notification.typeIcon)" />
            </el-icon>
          </div>
          <div class="content">
            <div class="item-header">
              <div class="title">
                {{ notification.title }}
              </div>
              <div class="time">
                {{ formatTime(notification.createdAt) }}
              </div>
            </div>
            <div class="message">
              {{ notification.content }}
            </div>
            <div class="type-tag">
              {{ notification.typeName }}
            </div>
          </div>
          <div class="actions">
            <el-tooltip content="删除">
              <el-icon
                class="delete-icon"
                @click.stop="handleDelete(notification.id)"
              >
                <Delete />
              </el-icon>
            </el-tooltip>
          </div>
        </div>
      </el-scrollbar>
    </div>
  </el-popover>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Bell, Delete, InfoFilled, WarningFilled, CircleCheckFilled } from '@element-plus/icons-vue'
import { deleteNotifications } from '@/api/notification'
import notificationService from '@/utils/notificationService'

// FontAwesome图标映射
const iconMap = {
  'bullhorn': InfoFilled,
  'tasks': CircleCheckFilled,
  'exclamation-triangle': WarningFilled,
  'database': InfoFilled,
  'clipboard-check': CircleCheckFilled
}

// 获取图标组件
const getIconComponent = (iconName) => {
  return iconMap[iconName] || InfoFilled
}

const router = useRouter()
const visible = ref(false)
const loading = ref(false)
const unreadCount = ref(0)
const notifications = ref([])
const activeTab = ref('all')
let unsubscribe = null

// 通知服务回调
const handleNotificationUpdate = (data) => {
  unreadCount.value = data.unreadCount
  
  // 如果弹窗打开状态，则不更新列表，避免用户正在查看的内容突然变化
  if (!visible.value) {
    notifications.value = data.notifications
  }
}

// 格式化时间
const formatTime = (timestamp) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diff = now - date
  
  // 小于1天，显示"x小时前"或"x分钟前"
  if (diff < 24 * 60 * 60 * 1000) {
    const hours = Math.floor(diff / (60 * 60 * 1000))
    if (hours > 0) {
      return `${hours}小时前`
    }
    const minutes = Math.floor(diff / (60 * 1000))
    return minutes > 0 ? `${minutes}分钟前` : '刚刚'
  }
  
  // 小于1周，显示周几
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    const days = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
    return days[date.getDay()]
  }
  
  // 大于1周，显示具体日期
  return `${date.getMonth() + 1}月${date.getDate()}日`
}

// 打开通知弹窗
const openPopover = () => {
  visible.value = true
  loading.value = true
  
  // 获取最新通知
  const params = {
    page: 1,
    pageSize: 10,
    ...(activeTab.value === 'unread' ? { isRead: false } : {})
  }
  
  notificationService.fetchNotifications(params)
    .finally(() => {
      loading.value = false
    })
}

// 弹窗关闭
const onHide = () => {
  visible.value = false
}

// 标记所有为已读
const handleMarkAllAsRead = async () => {
  if (unreadCount.value === 0) return
  
  try {
    await notificationService.markAllAsRead()
    ElMessage.success('已将所有通知标记为已读')
  } catch (error) {
    ElMessage.error('操作失败')
  }
}

// 点击通知项
const handleNotificationClick = async (notification) => {
  if (!notification.isRead) {
    try {
      await notificationService.markAsRead(notification.id)
    } catch (error) {
      console.error('标记已读失败:', error)
    }
  }
  
  // 如果有跳转链接，则跳转
  if (notification.actionUrl) {
    router.push(notification.actionUrl)
    visible.value = false
  }
}

// 删除通知
const handleDelete = async (id) => {
  try {
    const res = await deleteNotifications(id)
    if (res.code === 200) {
      ElMessage.success('删除成功')
      
      // 从列表中移除
      notifications.value = notifications.value.filter(item => item.id !== id)
      
      // 更新通知服务的数据
      notificationService.fetchUnreadCount()
      notificationService.fetchNotifications()
    }
  } catch (error) {
    console.error('删除通知失败:', error)
    ElMessage.error('删除失败')
  }
}

// 跳转到通知中心
const navigateToNotificationCenter = () => {
  router.push('/notification/center')
  visible.value = false
}

// 监听标签页变化
watch(activeTab, () => {
  if (visible.value) {
    loading.value = true
    
    const params = {
      page: 1,
      pageSize: 10,
      ...(activeTab.value === 'unread' ? { isRead: false } : {})
    }
    
    notificationService.fetchNotifications(params)
      .finally(() => {
        loading.value = false
      })
  }
})

onMounted(() => {
  // 订阅通知服务
  unsubscribe = notificationService.subscribe(handleNotificationUpdate)
  
  // 开始轮询通知
  notificationService.startPolling()
})

onUnmounted(() => {
  // 取消订阅
  if (unsubscribe) {
    unsubscribe()
  }
})
</script>

<style scoped>
.notification-badge :deep(.el-badge__content) {
  z-index: 1;
}

.notification-icon {
  font-size: 20px;
  cursor: pointer;
  transition: color 0.3s;
}

.notification-icon:hover {
  color: var(--el-color-primary);
}

:deep(.notification-popover) {
  padding: 0;
  overflow: hidden;
}

.notification-container {
  display: flex;
  flex-direction: column;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.notification-header .title {
  font-size: 16px;
  font-weight: 500;
}

.notification-tabs {
  padding: 0 10px;
}

.notification-loading {
  padding: 20px;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
  border-bottom: 1px solid var(--el-border-color-lighter);
  cursor: pointer;
  transition: background-color 0.3s;
  position: relative;
}

.notification-item:hover {
  background-color: var(--el-fill-color-light);
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-item.is-read {
  opacity: 0.8;
}

.icon-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  margin-right: 12px;
  flex-shrink: 0;
  color: white;
}

.icon-container.info {
  background-color: var(--el-color-info);
}

.icon-container.warning {
  background-color: var(--el-color-warning);
}

.icon-container.danger {
  background-color: var(--el-color-danger);
}

.content {
  flex: 1;
  min-width: 0;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
}

.item-header .title {
  font-weight: 500;
  font-size: 14px;
  color: var(--el-text-color-primary);
  margin-right: 8px;
}

.item-header .time {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  white-space: nowrap;
}

.message {
  font-size: 13px;
  color: var(--el-text-color-secondary);
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.type-tag {
  display: inline-block;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  background-color: var(--el-fill-color);
  color: var(--el-text-color-secondary);
}

.actions {
  display: flex;
  margin-left: 12px;
  opacity: 0;
  transition: opacity 0.2s;
}

.notification-item:hover .actions {
  opacity: 1;
}

.delete-icon {
  cursor: pointer;
  color: var(--el-text-color-secondary);
  transition: color 0.2s;
}

.delete-icon:hover {
  color: var(--el-color-danger);
}
</style> 